/**
 * SiteNav 互動：首頁 reveal-on-scroll / 內頁常駐釘頂 + hamburger drawer + focus trap
 *
 * nav 定位三態（由頁面類型決定，互斥）：
 *   - 首頁（存在 .hero-immersion）：沉浸 ↔ reveal-on-scroll，由 hero-immersion.js 既有 IO 驅動
 *     （它已觀察 .hero 做沉浸 ↔ reveal 交接）。本檔偵測到首頁即「不」介入定位，避免雙重觀察打架。
 *   - 內頁（.site-nav--persistent，預設）：常駐釘頂（CSS position:sticky）。內頁無沉浸底，
 *     若沿用「初始隱藏 + sentinel 滑入」→ 一打開頂部沒 menu、要捲動才出現（reveal 盲點）。
 *     故內頁常駐：一打開就可見可互動，本檔只補可見/可互動語意（去 inert），不啟動任何 reveal IO。
 *   - minimal（.site-nav--minimal，登入 / 註冊完成頁）：常駐靜態白條（CSS position:static），同上補語意。
 *
 * drawer：切換 aria-expanded + body scroll lock；關閉途徑 toggle / backdrop / ESC；
 *   focus trap（Tab 在 drawer 內循環，auto-focus 第一個元素）。
 *   drawer 開啟期間不收回、不 inert（否則焦點鎖在隱藏 nav）；關閉後 syncFromSentinel() 重新同步。
 *   內頁 / minimal 常駐 → syncFromSentinel 為 no-op；首頁由 hero IO 管。
 */

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const nav = document.querySelector('.site-nav');
const toggle = document.querySelector('.nav-toggle');
const drawer = document.querySelector('#nav-drawer');
const backdrop = document.querySelector('.nav-drawer__backdrop');

/* ---- nav 定位語意（首頁由 hero-immersion.js 驅動；內頁 / minimal 常駐） ---- */

// 對外暴露給 drawer 關閉時呼叫；常駐 nav（內頁 / minimal）為 no-op，首頁由 hero IO 管。
let syncFromSentinel = () => {};

if (nav) {
  // 首頁（存在 hero immersion 深底）→ 定位（沉浸 ↔ reveal）交給 hero-immersion.js 既有 IO，本檔不介入。
  // 其餘（內頁 .site-nav--persistent 常駐釘頂 / minimal 常駐靜態白條）→ CSS 已永遠可見，
  //   本檔僅補可見 / 可互動語意：去除 base 初始隱藏態殘留的 inert / aria-hidden，加 is-revealed 保語意一致。
  //   常駐 nav 不需要 reveal 觀察（無 sentinel IO、無 scrollY listener）—— 一打開頂部就有 menu。
  const isHome = !!document.querySelector('.hero-immersion');

  if (!isHome) {
    nav.classList.add('is-revealed');
    nav.removeAttribute('inert');
    nav.removeAttribute('aria-hidden');
  }
}

/* ---- nav active 高亮：從 location.pathname 推導目前分頁，對應 nav-link 加 active ----
 *
 * 與 _nav.html 既有 EJS（currentPage local）機制「同結果、互補」：build 時 EJS 已依檔名
 * 把 .nav-link--active + aria-current 烤進 6 頂層頁 + 4 detail 頁（NAV_PAGE_MAP 歸併）。
 * 本段在執行期依「實際 URL 第一段」重算同一份 active，對既有 10 頁為冪等（設同一項、清其餘，
 * 不與 EJS 打架）；對「未進 build 映射的新 clean URL / 巢狀 deep URL」則補上 EJS 烤不到的高亮。
 *
 * 推導法：取 pathname 第一段 segment（/about → about、/article-detail → article-detail），
 * 經 SECTION_MAP 歸併 detail 子頁到父分頁（對齊 vite.config NAV_PAGE_MAP），再比對各 nav-link
 * 的 href 第一段。命中→加 active + aria-current；其餘→清除（避免 EJS 與實況不一致時殘留雙 active）。
 * 首頁（home，不在六選單）→ 無項目 active，與 EJS currentPage='home' 一致。 */
if (nav) {
  // detail / compare clean URL 第一段 → 父分頁 slug（對齊 vite.config.js NAV_PAGE_MAP）。
  const SECTION_MAP = {
    'article-detail': 'articles',
    'clinic-detail': 'clinics',
    'product-detail': 'products',
    'product-compare': 'products'
  };

  // 取 pathname 第一段（去前導斜線、去尾段 / 與 .html）；空（首頁 /）→ ''。
  const firstSegment = window.location.pathname
    .replace(/^\/+/, '')
    .split('/')[0]
    .replace(/\.html$/, '');
  const activeSection = SECTION_MAP[firstSegment] || firstSegment;

  nav.querySelectorAll('.nav-link').forEach((link) => {
    // nav-link href 亦取第一段比對（href="/articles" → articles）。
    const linkSection = (link.getAttribute('href') || '')
      .replace(/^\/+/, '')
      .split('/')[0]
      .replace(/\.html$/, '');
    const isActive = linkSection !== '' && linkSection === activeSection;

    link.classList.toggle('nav-link--active', isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });
}

/* ---- Hamburger drawer + focus trap ---- */

if (toggle && drawer) {
  const getFocusables = () =>
    Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  // mobileMQ：drawer 進入手機模式（fixed slide-in）的斷點，與 site-nav.css 的
  // `@media (max-width: 1024px)`（drawer → fixed slide-in）精確對齊。matches=true → 手機 drawer 模式。
  // 桌面（不 match）時 .nav-drawer 為 display:contents 的 inline 桌面 nav 列，無「開啟」動作。
  const mobileMQ = window.matchMedia('(max-width: 1024px)');

  // 關閉時 drawer 雖 transform:translateX(100%) 移出畫面，仍 display:flex → 其 ~8 條連結
  // 留在 tab order + a11y tree（off-screen 焦點陷阱，S-17）。對 drawer 套 inert：手機模式關閉=inert
  // （移出 tab order 與 a11y tree），開啟=去 inert。toggle 在 drawer 外（兄弟節點）故不受影響。
  //
  // ⚠ inert 必須以斷點 gating（S-17 修復回歸）：桌面寬度時 .nav-drawer 是 display:contents 的
  // inline 桌面 nav 列（同一份 DOM），桌面無「開啟」動作 → 若沿用 inert=!open 會永遠 inert，
  // 整條桌面 nav 連結 / CTA 不可點不可聚焦（S-17 批 1 導致的桌面 nav 死板回歸）。
  // 規則：桌面（不 match mobileMQ）→ inert 恆 false；手機（match）→ inert = !open（關閉→inert）。
  const inertForState = (open) => (mobileMQ.matches ? !open : false);

  // 依目前「是否開啟」重算並套用 inert（桌面恆 false / 手機看開合）。供 init / setExpanded / 斷點切換共用。
  const syncInert = () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    drawer.inert = inertForState(open);
  };

  // 初始 markup 為關閉態 → 依當前斷點決定 inert（手機=true、桌面=false）。
  syncInert();

  const setExpanded = (open) => {
    toggle.setAttribute('aria-expanded', String(open));
    drawer.classList.toggle('nav-drawer--open', open);
    drawer.inert = inertForState(open);
    nav?.classList.toggle('site-nav--menu-open', open);
    document.body.style.overflow = open ? 'hidden' : '';

    if (open) {
      // drawer 開啟必確保 nav 可見可互動（捲動中也可能開）：解除隱藏態，避免焦點落在畫面外。
      nav?.classList.add('is-revealed');
      nav?.removeAttribute('inert');
      nav?.removeAttribute('aria-hidden');
      // 等 transition / display 切換後再 focus（avoid invisible focus）
      requestAnimationFrame(() => {
        const focusables = getFocusables();
        focusables[0]?.focus();
      });
    } else {
      toggle.focus();
      // 關閉後重新同步定位狀態：首頁 / 內頁常駐 / minimal 皆 no-op（各自定位不靠 drawer）。
      // 保留呼叫點以維持「drawer 關閉 → 同步」語意，未來若重啟 reveal 觀察可在此掛回。
      syncFromSentinel();
    }
  };

  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    setExpanded(!isOpen);
  });

  // backdrop click 關閉
  backdrop?.addEventListener('click', () => {
    setExpanded(false);
  });

  // ESC 關閉 + Tab focus trap
  document.addEventListener('keydown', (e) => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    if (!isOpen) return;

    if (e.key === 'Escape') {
      setExpanded(false);
      return;
    }

    if (e.key === 'Tab') {
      const focusables = getFocusables();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // 跨斷點時：(1) 回桌面寬度自動收合（避免 stuck-open 狀態）(2) 重新同步 inert
  // （桌面恆 false / 手機看開合）—— 確保 resize 桌面↔手機後 inert 不卡死。
  // setExpanded(false) 內已呼叫 drawer.inert = inertForState(false)，故收合分支已同步；
  // 其餘情況（已關閉、或反向 → 手機）顯式 syncInert() 補上。
  mobileMQ.addEventListener('change', (e) => {
    if (!e.matches) {
      // → 桌面：收合（含 overflow / aria-expanded 還原）並去 inert（setExpanded 內處理）。
      setExpanded(false);
    } else {
      // → 手機：依目前開合重算 inert（剛切到手機且關閉 → inert=true）。
      syncInert();
    }
  });
}
